/**
 * Fase 2 — matriz data-driven de documentos requeridos: dado el sector del sujeto
 * obligado (`tenants.entity_mode`), el tipo de entidad del cliente y, para las
 * personas relacionadas, su rol/posición → la lista de tipos de documento exigidos.
 *
 * Tablas planas en TS (no en DB): se comparten y versionan con el código (web + api),
 * de modo que recalibrar una regla es un cambio revisable, no una migración. Toda
 * cadena de cara al usuario es es-PA; los labels se espejan en `i18n/es-PA.ts`
 * (namespace `docTypes`) siguiendo la convención del catálogo.
 *
 * Fundamento regulatorio: Ley 23/2015 + Decreto Ejecutivo 363/2015 (DDC mínimo
 * uniforme) + Acuerdos por sector. Los umbrales y las listas sector-específicas
 * provienen de investigación regulatoria (confirmados por título, no línea a línea):
 * son perilla de calibración, no constantes de producto — ver los `// ponytail:`.
 */
/** Catálogo de tipos de documento (claves estables; labels es-PA en DOC_TYPE_LABELS). */
export const DOC_TYPE_KEYS = [
    'cedula_pasaporte',
    'comprobante_domicilio',
    'carta_referencia_bancaria',
    'carta_referencia_profesional',
    'declaracion_pep',
    'fuente_fondos',
    'pacto_social',
    'certificado_registro_publico',
    'ruc_aviso_operacion',
    'registro_accionistas',
    'poder_acta_junta',
    'organigrama_estructura',
    'estados_financieros',
    'formulario_kyc_firmado',
    'certificados_acciones',
    'acta_fundacional',
    'declaracion_jurada_bf',
    'fatca_crs',
    'perfil_idoneidad',
    'datos_poliza',
    'datos_inmueble',
    'forma_pago',
];
/**
 * Labels es-PA — fuente de verdad para las ranuras documentales (web + api). Se
 * espeja en `esPA.docTypes` (i18n) por convención del catálogo; mantener en sincronía
 * (un test lo verifica).
 */
export const DOC_TYPE_LABELS = {
    cedula_pasaporte: 'Cédula o pasaporte',
    comprobante_domicilio: 'Comprobante de domicilio',
    carta_referencia_bancaria: 'Carta de referencia bancaria (indica producto vigente o cliente valioso)',
    carta_referencia_profesional: 'Carta de referencia profesional',
    declaracion_pep: 'Declaración de persona expuesta políticamente (PEP)',
    fuente_fondos: 'Declaración de fuente de fondos',
    pacto_social: 'Pacto social',
    certificado_registro_publico: 'Certificado del Registro Público',
    ruc_aviso_operacion: 'RUC / Aviso de Operación',
    registro_accionistas: 'Registro de accionistas',
    poder_acta_junta: 'Poder o acta de junta directiva',
    organigrama_estructura: 'Organigrama de la estructura de propiedad',
    estados_financieros: 'Estados financieros',
    formulario_kyc_firmado: 'Formulario "Conozca a su Cliente" firmado',
    certificados_acciones: 'Certificados de acciones',
    acta_fundacional: 'Acta fundacional',
    declaracion_jurada_bf: 'Declaración jurada de beneficiario final',
    fatca_crs: 'Autocertificación FATCA/CRS (W-8/W-9)',
    perfil_idoneidad: 'Perfil de idoneidad (suitability)',
    datos_poliza: 'Datos de la póliza',
    datos_inmueble: 'Datos del inmueble',
    forma_pago: 'Forma de pago',
};
/**
 * Umbral de participación (%) que auto-marca a un accionista como Beneficiario Final
 * por sector.
 * // ponytail: umbrales de investigación regulatoria (Acuerdo 1-2026 SBP 10%; SSRP 10%;
 * resto 25%) — perilla de calibración, validar con cumplimiento antes de tratarlos como
 * normativos. (Fundación usa `posicion=primario`, no umbral.)
 */
export const UBO_THRESHOLD_BY_SECTOR = {
    financiero: 10,
    seguros: 10,
    valores: 25,
    inmobiliaria: 25,
    abogados: 25,
};
/** `true` cuando pct alcanza el umbral de UBO del sector (auto-marca Beneficiario Final). */
export function isUboByOwnership(pct, sector) {
    return pct >= UBO_THRESHOLD_BY_SECTOR[sector];
}
/**
 * Documentos exigidos a una persona relacionada (director / accionista sobre umbral /
 * BF / fundador / consejo primario). El set base es igual para todo rol de persona
 * natural; el sector añade FATCA/CRS.
 */
export function requiredDocsForPerson(input) {
    const { sector, entityType, posicion, conValorRescate, usPerson } = input;
    // Fundación es patrimonial (como un testamento): a los beneficiarios SECUNDARIOS no
    // se les pide nada. Criterio Sociedad = umbral de %; criterio Fundación = posición.
    if (entityType === 'fundacion' && posicion === 'secundario')
        return [];
    const docs = [
        'cedula_pasaporte',
        'carta_referencia_bancaria',
        'carta_referencia_profesional',
        'comprobante_domicilio',
        'declaracion_pep',
    ];
    // FATCA/CRS (Ley 47/2016, Ley 51/2016): solo instituciones que llevan cuenta. Valores
    // y financiero siempre; seguros solo con valor de rescate.
    // ponytail: aplicabilidad por sector = investigación regulatoria, calibrable.
    if (usPerson === true ||
        sector === 'valores' ||
        sector === 'financiero' ||
        (sector === 'seguros' && conValorRescate === true)) {
        docs.push('fatca_crs');
    }
    return dedupe(docs);
}
// Documentos de nivel entidad por tipo de cliente (add-ons sectoriales aparte).
const SOCIEDAD_ANONIMA_DOCS = [
    'pacto_social',
    'certificado_registro_publico',
    'ruc_aviso_operacion',
    'registro_accionistas',
    'poder_acta_junta',
    'organigrama_estructura',
    'estados_financieros',
    'formulario_kyc_firmado',
    'certificados_acciones',
    'declaracion_jurada_bf',
];
const ENTITY_BASE_DOCS = {
    persona_natural: [
        'cedula_pasaporte',
        'comprobante_domicilio',
        'carta_referencia_bancaria',
        'carta_referencia_profesional',
        'declaracion_pep',
        'fuente_fondos',
        'formulario_kyc_firmado',
    ],
    sociedad_anonima: SOCIEDAD_ANONIMA_DOCS,
    // Fundación = Sociedad SIN certificados_acciones ni registro_accionistas, + acta_fundacional.
    fundacion: [
        ...SOCIEDAD_ANONIMA_DOCS.filter((d) => d !== 'certificados_acciones' && d !== 'registro_accionistas'),
        'acta_fundacional',
    ],
};
/**
 * Documentos exigidos a nivel de la entidad-cliente (el formulario de la entidad),
 * = base por tipo + add-ons por sector + add-ons por condición del cliente.
 */
export function requiredDocsForEntity(input) {
    const { sector, entityType, condiciones } = input;
    return dedupe([
        ...ENTITY_BASE_DOCS[entityType],
        ...entityAddOnsForSector(sector, entityType),
        ...condicionAddOns(sector, condiciones),
    ]);
}
/** Add-ons por condición del cliente (idénticos para PN y PJ; dedupe absorbe la base PN). */
function condicionAddOns(sector, c) {
    if (!c)
        return [];
    const docs = [];
    // PEP → debida diligencia ampliada: declaración PEP + fuente de fondos/riqueza.
    if (c.pep)
        docs.push('declaracion_pep', 'fuente_fondos');
    // US person → FATCA aplica sin importar el sector.
    if (c.usPerson)
        docs.push('fatca_crs');
    // Extranjero → comprobante de domicilio también a nivel de la entidad.
    if (c.extranjero)
        docs.push('comprobante_domicilio');
    // Seguros con valor de rescate → institución que "lleva cuenta" (espejo de la regla por persona).
    if (c.conValorRescate && sector === 'seguros')
        docs.push('fatca_crs');
    return docs;
}
const ENTITY_TYPE_LABELS = {
    persona_natural: 'Persona Natural',
    sociedad_anonima: 'Sociedad Anónima',
    fundacion: 'Fundación de Interés Privado',
};
const CONDICION_SUFFIX = [
    ['pep', 'PEP'],
    ['usPerson', 'US person'],
    ['extranjero', 'Extranjero'],
    ['conValorRescate', 'Con valor de rescate'],
];
/**
 * La plantilla KYC efectiva para un cliente: se resuelve sola a partir del sector
 * del tenant + tipo de cliente + condiciones ya respondidas en el asistente, de
 * modo que el registro arma el checklist documental sin pasos adicionales.
 */
export function kycPlantilla(input) {
    const { sector, entityType, condiciones } = input;
    const activas = CONDICION_SUFFIX.filter(([k]) => condiciones?.[k] === true);
    return {
        key: [sector, entityType, ...activas.map(([k]) => k)].join('.'),
        nombre: [ENTITY_TYPE_LABELS[entityType], ...activas.map(([, s]) => s)].join(' · '),
        docs: requiredDocsForEntity(input),
    };
}
/**
 * Add-ons documentales por sector a nivel entidad.
 * // ponytail: las listas sector-específicas provienen de investigación regulatoria
 * (Acuerdos por sector) y son calibrables — confirmar con el owner antes de tratarlas
 * como normativas. Para persona natural el único add-on sectorial es FATCA/CRS; los
 * bloques de objeto-de-transacción (póliza/inmueble) cuelgan del alta de la persona
 * jurídica.
 */
function entityAddOnsForSector(sector, entityType) {
    if (entityType === 'persona_natural') {
        return sector === 'financiero' || sector === 'valores' ? ['fatca_crs'] : [];
    }
    switch (sector) {
        case 'valores':
            return ['perfil_idoneidad', 'fatca_crs'];
        case 'seguros':
            return ['datos_poliza'];
        case 'inmobiliaria':
            return ['datos_inmueble', 'forma_pago'];
        case 'financiero':
            return ['fatca_crs'];
        case 'abogados':
            return [];
    }
}
/** De-duplica preservando el primer orden de aparición. */
function dedupe(docs) {
    return [...new Set(docs)];
}
//# sourceMappingURL=required-docs.js.map